/**
 * Pure transforms from Claude Code transcript JSONL records into compact
 * `PtyChatEventPayload`s for the experimental chat PTY proxy.
 *
 * Deliberately separate from `record-mapper.ts`: that module's output must
 * stay in SDK parity (its contract is test-pinned), while this one is a lossy,
 * chat-shaped projection — it keeps user prompts (which the harness mapper
 * drops), truncates aggressively, and filters transcript noise.
 *
 * Transcript files are a trusted on-disk artifact written by the `claude`
 * CLI, so parsing their records is allowed — unlike raw PTY stdout, which is
 * never parsed.
 */

import type { PtyChatEventPayload } from "@project/shared";

// Headroom under the wire-schema caps (16_384 / 2_000) so a truncation
// ellipsis can never push a payload over the Zod limit server-side.
const TEXT_MAX = 16_000;
const TOOL_INPUT_MAX = 1_900;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function stringField(record: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
  }
  return undefined;
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** Local slash-command wrapper records — TUI plumbing, not conversation. */
function isCommandWrapperText(text: string): boolean {
  const trimmed = text.trimStart();
  return trimmed.startsWith("<command-name>") || trimmed.startsWith("<local-command-");
}

function mapSystem(record: Record<string, unknown>): PtyChatEventPayload[] {
  if (record.subtype === "init") {
    const event: PtyChatEventPayload = {
      kind: "init",
      model: stringField(record, "model") ?? "",
    };
    const sessionId = stringField(record, "session_id", "sessionId");
    if (sessionId !== undefined) event.claudeSessionId = sessionId;
    return [event];
  }
  // The interactive CLI marks a completed turn with a `turn_duration` system
  // record, not a `result` record — same mapping as record-mapper.ts.
  if (record.subtype === "turn_duration") return [{ kind: "turn_end" }];
  return [];
}

function mapAssistant(record: Record<string, unknown>): PtyChatEventPayload[] {
  const message = record.message;
  if (!isRecord(message)) return [];
  const content: unknown[] = isUnknownArray(message.content) ? message.content : [];
  const events: PtyChatEventPayload[] = [];
  for (const raw of content) {
    if (!isRecord(raw)) continue;
    if (raw.type === "text") {
      const text = stringField(raw, "text");
      if (text && text.length > 0) {
        events.push({ kind: "assistant_text", text: truncate(text, TEXT_MAX) });
      }
    } else if (raw.type === "tool_use") {
      const name = stringField(raw, "name");
      if (name) {
        const input = "input" in raw ? raw.input : undefined;
        events.push({
          kind: "tool_use",
          name: truncate(name, 200),
          input: JSON.stringify(input ?? {}).slice(0, TOOL_INPUT_MAX),
        });
      }
    }
  }
  return events;
}

function mapUser(record: Record<string, unknown>): PtyChatEventPayload[] {
  const message = record.message;
  if (!isRecord(message)) return [];
  const content = message.content;

  let text: string;
  if (typeof content === "string") {
    text = content;
  } else if (isUnknownArray(content)) {
    // Tool results ride user-role records; they are outputs, not prompts.
    const hasToolResult = content.some((b) => isRecord(b) && b.type === "tool_result");
    if (hasToolResult) return [];
    text = content
      .filter((b): b is Record<string, unknown> => isRecord(b) && b.type === "text")
      .map((b) => (typeof b.text === "string" ? b.text : ""))
      .filter((t) => t.length > 0)
      .join("\n");
  } else {
    return [];
  }

  const trimmed = text.trim();
  if (trimmed.length === 0 || isCommandWrapperText(trimmed)) return [];
  return [{ kind: "user_text", text: truncate(trimmed, TEXT_MAX) }];
}

/**
 * Map one parsed transcript record to zero-or-more chat events (a record can
 * hold several content blocks). Unknown/noise records map to `[]`.
 */
export function mapChatRecords(raw: unknown): PtyChatEventPayload[] {
  if (!isRecord(raw)) return [];
  // Subagent (sidechain) traffic and meta records are transcript noise for a
  // chat surface — the main-thread records already narrate the turn.
  if (raw.isSidechain === true || raw.isMeta === true) return [];
  switch (raw.type) {
    case "system":
      return mapSystem(raw);
    case "assistant":
      return mapAssistant(raw);
    case "user":
      return mapUser(raw);
    case "result":
      return [{ kind: "turn_end" }];
    default:
      return [];
  }
}
